import { Target, Gift, Check, Lock, Calendar } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { useTheme } from "@/contexts/ThemeContext";
import { useCountUp } from "@/hooks/useCountUp";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import type { Goal } from "@/lib/services/store.service";

interface GoalWidgetProps {
  goal: Goal;
}

export function GoalWidget({ goal }: GoalWidgetProps) {
  const { config } = useTheme();
  const { t } = useTranslation();
  const { ref, isVisible } = useScrollReveal({ threshold: 0.3 });
  const [animatedPercentage, setAnimatedPercentage] = useState(0);

  const currentAmount = Number(goal.currentAmount) || 0;

  const animatedCurrent = useCountUp({
    start: 0,
    end: currentAmount,
    duration: 2000,
    delay: 300,
    shouldStart: isVisible,
    // Dinheiro: o valor final precisa dos centavos.
    decimals: 2,
  });

  const realPercentage = Math.min(
    100,
    (currentAmount / goal.targetAmount) * 100,
  );

  useEffect(() => {
    if (!isVisible) return;

    const timeout = setTimeout(() => {
      setAnimatedPercentage(realPercentage);
    }, 300);

    return () => clearTimeout(timeout);
  }, [realPercentage, isVisible]);

  const formatPrice = (price: number) => {
    const validPrice = isNaN(price) || !isFinite(price) ? 0 : price;

    return new Intl.NumberFormat(config?.language || "pt-BR", {
      style: "currency",
      currency: config?.currency?.code || "BRL",
    }).format(validPrice);
  };

  const formatDateShort = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(config?.language || "pt-BR", {
      day: "2-digit",
      month: "short",
    });
  };

  const sortedRewards = [...goal.rewards].sort((a, b) => a.order - b.order);

  return (
    <Card ref={ref} className="bg-card border-border overflow-hidden">
      {/* Header with gradient */}
      <div className="bg-gradient-primary px-4 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Target className="w-5 h-5 text-primary-foreground" />
            <span className="font-semibold text-primary-foreground">
              {t("store.widgets.goal.title")}
            </span>
          </div>
          <Badge
            variant="secondary"
            className="bg-white/20 text-white border-0"
          >
            <Calendar className="w-3 h-3 mr-1" />
            {formatDateShort(goal.startsAt)} - {formatDateShort(goal.endsAt)}
          </Badge>
        </div>
      </div>

      <CardContent className="p-4 space-y-4">
        {/* Progress */}
        <div>
          <div className="flex items-end justify-between mb-2">
            <div>
              <p className="text-sm text-muted-foreground">
                {t("store.widgets.goal.collected")}
              </p>
              <p className="font-display font-bold text-2xl text-foreground tabular-nums">
                {formatPrice(animatedCurrent)}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-muted-foreground">
                {t("store.widgets.goal.target")}
              </p>
              <p className="font-display font-semibold text-lg text-primary">
                {formatPrice(goal.targetAmount)}
              </p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="relative">
            <Progress
              value={animatedPercentage}
              className="h-3 transition-all duration-1000 ease-out"
            />
            <div
              className="absolute -top-1 transform -translate-x-1/2 transition-all duration-1000 ease-out"
              style={{ left: `${Math.min(animatedPercentage, 98)}%` }}
            >
              <div className="w-5 h-5 rounded-full bg-primary border-2 border-background shadow-glow flex items-center justify-center animate-pulse">
                <span className="text-[8px] font-bold text-primary-foreground">
                  {Math.round(animatedPercentage)}%
                </span>
              </div>
            </div>
          </div>

          <p className="text-xs text-muted-foreground text-center mt-2">
            {t("store.widgets.goal.description")}
          </p>
        </div>

        {/* Rewards */}
        {sortedRewards.length > 0 && (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">
              {t("store.widgets.goal.rewards")}
            </p>
            {sortedRewards.map((reward, index) => {
              const isUnlocked = currentAmount >= reward.targetAmount;

              return (
                <div
                  key={index}
                  className={`flex items-center gap-3 p-2 rounded-lg border ${
                    isUnlocked
                      ? "bg-accent/10 border-accent/30"
                      : "bg-muted/30 border-border"
                  }`}
                >
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center ${
                      isUnlocked
                        ? "bg-accent text-accent-foreground"
                        : "bg-muted text-muted-foreground"
                    }`}
                  >
                    {isUnlocked ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Lock className="w-4 h-4" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-sm font-medium ${
                        isUnlocked ? "text-foreground" : "text-muted-foreground"
                      }`}
                    >
                      {reward.description}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {formatPrice(reward.targetAmount)}
                    </p>
                  </div>
                  {isUnlocked && <Gift className="w-4 h-4 text-accent" />}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default GoalWidget;
